import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, ChevronDown, ChevronUp, Loader2, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useMesProductMutations, useMesProductOptionLists, useMesProductReference } from '@/hooks/useMesProducts'
import { cn } from '@/lib/utils'
import type { MesProductOption, MesProductOptionField } from '@/types/cgt'

/** The options as saved: trimmed, blank rows dropped. */
function cleaned(options: MesProductOption[]): MesProductOption[] {
  return options.map(o => ({ id: o.id, label: o.label.trim() })).filter(o => o.label.length > 0)
}

function same(a: MesProductOption[], b: MesProductOption[]) {
  return a.length === b.length && a.every((o, i) => o.id === b[i].id && o.label === b[i].label)
}

/**
 * Configure → Product Code → Option Sets (z8nrz7fvhx): the choices each
 * Product Code selection field offers. These lists serve Product Codes only;
 * the Option Sets tab stays the forms' own. Every field has exactly one list,
 * so there is nothing to add or delete here, only options to edit.
 *
 * Each field keeps its unsaved rows while another is open; Save writes the
 * one on screen. Without product_codes.manage it reads only.
 */
export function ProductCodeOptionSetsDialog({
  open,
  onOpenChange,
  canEdit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  canEdit: boolean
}) {
  const lists = useMesProductOptionLists(open)
  const reference = useMesProductReference(open)
  const { updateOptionList } = useMesProductMutations()
  const adrOther = reference.data?.adrIataOther ?? 'Other'

  const [selected, setSelected] = useState<MesProductOptionField>('productType')
  const [drafts, setDrafts] = useState<Partial<Record<MesProductOptionField, MesProductOption[]>>>({})
  const [error, setError] = useState<string | null>(null)

  // A fresh start each time it opens: what was left unsaved is dropped.
  useEffect(() => {
    if (open) {
      setDrafts({})
      setError(null)
    }
  }, [open])

  const saved = useMemo(
    () => new Map((lists.data ?? []).map(l => [l.field, l])),
    [lists.data],
  )
  const list = saved.get(selected)
  const rows = drafts[selected] ?? list?.options ?? []
  const dirty = (field: MesProductOptionField) => {
    const draft = drafts[field]
    return !!draft && !same(cleaned(draft), saved.get(field)?.options ?? [])
  }

  function setRows(next: MesProductOption[]) {
    setDrafts(d => ({ ...d, [selected]: next }))
    setError(null)
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir
    if (target < 0 || target >= rows.length) return
    const next = [...rows]
    ;[next[index], next[target]] = [next[target], next[index]]
    setRows(next)
  }

  function save() {
    const options = cleaned(rows)
    const labels = options.map(o => o.label.toLowerCase())
    const twice = options.find((o, i) => labels.indexOf(o.label.toLowerCase()) !== i)
    if (twice) {
      setError(`"${twice.label}" is on the list twice.`)
      return
    }
    setError(null)
    const field = selected
    updateOptionList.mutate(
      { field, options },
      {
        onSuccess: () =>
          setDrafts(d => {
            const next = { ...d }
            delete next[field]
            return next
          }),
        onError: e => setError(e instanceof Error ? e.message : 'Could not save the list.'),
      },
    )
  }

  const lockedOther = (o: MesProductOption) => selected === 'adrIataClassification' && o.label === adrOther

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Product Code option sets</DialogTitle>
          <DialogDescription>
            The choices each Product Code selection field offers. Used by Product Codes only, separate from the
            forms&apos; Option Sets.
          </DialogDescription>
        </DialogHeader>

        {lists.isLoading && (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {lists.isError && (
          <div className='flex items-center gap-2 py-6 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            Could not load the option sets. {lists.error.message}
          </div>
        )}

        {lists.data && (
          <div className='grid gap-4 sm:grid-cols-[220px_1fr]'>
            <nav aria-label='Selection fields' className='flex flex-col gap-0.5'>
              {lists.data.map(l => (
                <button
                  key={l.field}
                  type='button'
                  aria-current={l.field === selected ? 'true' : undefined}
                  onClick={() => {
                    setSelected(l.field)
                    setError(null)
                  }}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm',
                    l.field === selected ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:bg-muted/50',
                  )}
                >
                  <span className='truncate'>{l.name}</span>
                  <span className='flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground'>
                    {dirty(l.field) && <span className='h-1.5 w-1.5 rounded-full bg-primary' title='Unsaved changes' />}
                    {(drafts[l.field] ? cleaned(drafts[l.field]!) : l.options).length}
                  </span>
                </button>
              ))}
            </nav>

            <div className='flex min-w-0 flex-col gap-2'>
              <div>
                <h3 className='text-sm font-semibold text-foreground'>{list?.name}</h3>
                <p className='text-xs text-muted-foreground'>
                  {selected === 'adrIataClassification'
                    ? `"${adrOther}" stays on this list: it opens ADR/IATA Classification (if Other). `
                    : ''}
                  A product keeps its value when that option is renamed or removed here.
                </p>
              </div>

              {rows.length === 0 && (
                <p className='rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground'>
                  No options yet.
                </p>
              )}

              {rows.map((opt, i) => (
                <div key={opt.id} className='flex items-center gap-1.5'>
                  <Input
                    value={opt.label}
                    placeholder={`Option ${i + 1}`}
                    aria-label={`Option ${i + 1}`}
                    disabled={!canEdit || lockedOther(opt)}
                    onChange={e => {
                      const next = [...rows]
                      next[i] = { ...opt, label: e.target.value }
                      setRows(next)
                    }}
                  />
                  {canEdit && (
                    <>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='shrink-0'
                        disabled={i === 0}
                        aria-label='Move option up'
                        onClick={() => move(i, -1)}
                      >
                        <ChevronUp className='h-4 w-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='shrink-0'
                        disabled={i === rows.length - 1}
                        aria-label='Move option down'
                        onClick={() => move(i, 1)}
                      >
                        <ChevronDown className='h-4 w-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='shrink-0'
                        disabled={lockedOther(opt)}
                        aria-label='Remove option'
                        onClick={() => setRows(rows.filter((_, j) => j !== i))}
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </>
                  )}
                </div>
              ))}

              {canEdit && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='self-start'
                  onClick={() => setRows([...rows, { id: crypto.randomUUID(), label: '' }])}
                >
                  <Plus className='mr-1.5 h-3.5 w-3.5' /> Add option
                </Button>
              )}

              {error && <p className='text-sm text-destructive'>{error}</p>}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {canEdit && (
            <Button type='button' disabled={!dirty(selected) || updateOptionList.isPending} onClick={save}>
              {updateOptionList.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
